import React, { useRef, useEffect, useState } from 'react'
import Button from '@toasttab/shared/src/components/atoms/Button/Button'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import { resolvePrefix } from '@toasttab/shared/src/utils/assetUtils'
import useWindowSize from '@toasttab/shared/src/hooks/useWindowSize'

const BusinessSegmentations = ({
  header,
  body
}: {
  header: any
  body: any
}) => {
  const { width } = useWindowSize()
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isInView, setIsInView] = useState(false)
  const [videoLoaded, setVideoLoaded] = useState(false)

  const isMobile = width < 768

  useEffect(() => {
    if (!isMobile && videoRef.current) {
      let observer: IntersectionObserver | null = null

      // Dynamically import IntersectionObserver logic only when not mobile
      const setupObserver = async () => {
        if ('IntersectionObserver' in window && videoRef.current) {
          observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
              setIsInView(true)
              // Once video is in view, we can stop observing
              if (observer) {
                observer.disconnect()
              }
            }
          })
          observer.observe(videoRef.current)
        }
      }

      setupObserver()

      return () => {
        if (observer) {
          observer.disconnect()
        }
      }
    }
  }, [isMobile])

  useEffect(() => {
    if (!isMobile && isInView && !videoLoaded) {
      setVideoLoaded(true)
    }
  }, [isMobile, isInView, videoLoaded])

  useEffect(() => {
    if (!isMobile && videoLoaded && videoRef.current) {
      // Play video once it's loaded
      videoRef.current.play()
    }
  }, [isMobile, videoLoaded])
  return (
    <section className='bg-gray-100 mb-10 lg:mb-16 relative rounded-[1.5rem] lg:rounded-[3rem]'>
      <picture>
        <source
          sizes='(max-width: 768px) 100vw, 50vw'
          srcSet='https://images.ctfassets.net/tgv5pu8pgmos/6j74Z1QPjB45u22KklxXfC/e9f2ceec7d1f8834557ef01bd96e374a/solutions-background.png?fm=avif&w=320&fit=scale 320w, https://images.ctfassets.net/tgv5pu8pgmos/6j74Z1QPjB45u22KklxXfC/e9f2ceec7d1f8834557ef01bd96e374a/solutions-background.png?fm=avif&w=640&fit=scale 640w, https://images.ctfassets.net/tgv5pu8pgmos/6j74Z1QPjB45u22KklxXfC/e9f2ceec7d1f8834557ef01bd96e374a/solutions-background.png?fm=avif&w=768&fit=scale 768w, https://images.ctfassets.net/tgv5pu8pgmos/6j74Z1QPjB45u22KklxXfC/e9f2ceec7d1f8834557ef01bd96e374a/solutions-background.png?fm=avif&w=1024&fit=scale 1024w, https://images.ctfassets.net/tgv5pu8pgmos/6j74Z1QPjB45u22KklxXfC/e9f2ceec7d1f8834557ef01bd96e374a/solutions-background.png?fm=avif&w=1280&fit=scale 1280w, https://images.ctfassets.net/tgv5pu8pgmos/6j74Z1QPjB45u22KklxXfC/e9f2ceec7d1f8834557ef01bd96e374a/solutions-background.png?fm=avif&w=1536&fit=scale 1536w, https://images.ctfassets.net/tgv5pu8pgmos/6j74Z1QPjB45u22KklxXfC/e9f2ceec7d1f8834557ef01bd96e374a/solutions-background.png?fm=avif&w=1920&fit=scale 1920w'
          type='image/avif'
        />
        <source
          sizes='(max-width: 768px) 100vw, 50vw'
          srcSet='https://images.ctfassets.net/tgv5pu8pgmos/6j74Z1QPjB45u22KklxXfC/e9f2ceec7d1f8834557ef01bd96e374a/solutions-background.png?fm=webp&w=320&fit=scale 320w, https://images.ctfassets.net/tgv5pu8pgmos/6j74Z1QPjB45u22KklxXfC/e9f2ceec7d1f8834557ef01bd96e374a/solutions-background.png?fm=webp&w=640&fit=scale 640w, https://images.ctfassets.net/tgv5pu8pgmos/6j74Z1QPjB45u22KklxXfC/e9f2ceec7d1f8834557ef01bd96e374a/solutions-background.png?fm=webp&w=768&fit=scale 768w, https://images.ctfassets.net/tgv5pu8pgmos/6j74Z1QPjB45u22KklxXfC/e9f2ceec7d1f8834557ef01bd96e374a/solutions-background.png?fm=webp&w=1024&fit=scale 1024w, https://images.ctfassets.net/tgv5pu8pgmos/6j74Z1QPjB45u22KklxXfC/e9f2ceec7d1f8834557ef01bd96e374a/solutions-background.png?fm=webp&w=1280&fit=scale 1280w, https://images.ctfassets.net/tgv5pu8pgmos/6j74Z1QPjB45u22KklxXfC/e9f2ceec7d1f8834557ef01bd96e374a/solutions-background.png?fm=webp&w=1536&fit=scale 1536w, https://images.ctfassets.net/tgv5pu8pgmos/6j74Z1QPjB45u22KklxXfC/e9f2ceec7d1f8834557ef01bd96e374a/solutions-background.png?fm=webp&w=1920&fit=scale 1920w'
          type='image/webp'
        />
        <img
          alt='solutions background'
          className='absolute top-0 lazyload left-0 w-full h-full object-cover rounded-[1.5rem] lg:rounded-[3rem] md:hidden'
          src='https://images.ctfassets.net/tgv5pu8pgmos/6j74Z1QPjB45u22KklxXfC/e9f2ceec7d1f8834557ef01bd96e374a/solutions-background.png'
        />
      </picture>
      {!isMobile && (
        <video
          className='absolute top-0 left-0 w-full h-full object-cover rounded-[1.5rem] lg:rounded-[3rem]'
          loop
          muted
          playsInline
          poster='https://images.ctfassets.net/tgv5pu8pgmos/6j74Z1QPjB45u22KklxXfC/e9f2ceec7d1f8834557ef01bd96e374a/solutions-background.png?fm=webp'
          preload='none'
          ref={videoRef}
        >
          {videoLoaded ? (
            <>
              <source
                src='https://d2w1ef2ao9g8r9.cloudfront.net/images/videos/business-component-broll-2.webm'
                type='video/webm'
              />
              <source
                src='https://d2w1ef2ao9g8r9.cloudfront.net/images/videos/business-component-broll.mp4'
                type='video/mp4'
              />
            </>
          ) : null}
          Your browser does not support the video tag.
        </video>
      )}

      <div className='ds-wrapper relative z-10'>
        <div className='ds-container lg:ds-grid'>
          <div className='col-span-4 lg:col-span-12 py-10 lg:py-36'>
            <div className='flex flex-col items-center max-w-[342px] lg:max-w-[46.5rem] mb-6 lg:mb-10 mx-auto text-center'>
              <Typography
                as='h2'
                classes='font-effra font-base !leading-[1.688rem] lg:!leading-[3rem] mb-2 lg:mb-4 !text-[1.938rem] lg:!text-[45px] text-white'
                variation='h2'
              >
                {header.heading}
              </Typography>
              <Typography
                as='p'
                classes='font-effra font-base !leading-[1.688rem] lg:!leading-[2rem] !text-[1.375rem] lg:!text-[1.625rem] text-white'
                variation='default'
              >
                {header.description}
              </Typography>
            </div>
            <div className='flex justify-center items-center flex-wrap gap-4 lg:gap-x-2 lg:gap-y-4 w-[343px] lg:w-[936px] mx-auto'>
              {body.segments.map((segment: any, i: number) => {
                return (
                  <Button
                    classes='w-[343px] md:!w-[343px] lg:!w-auto'
                    heapInteraction={segment.heapInteraction}
                    href={segment.url}
                    key={`segment-${i}`}
                    variation='pill'
                  >
                    {segment.label}
                    <img
                      alt='white cheveron right'
                      className='ml-auto lg:ml-1'
                      src={resolvePrefix('/svg/chevron-right-white.svg')}
                    />
                  </Button>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default BusinessSegmentations
